<script lang="ts">
  import { searchState, search } from '../stores/debugger-timeline';
  import { goHome } from '../stores/router';
  import type { SearchType, Environment } from '../types/debugger';
  import { animate } from 'motion';
  import { onMount } from 'svelte';

  let query = $state('');
  let searchType = $state<SearchType>('advertiser_id');
  let env = $state<Environment>('Beta');
  let searchBarEl: HTMLDivElement | undefined = $state();

  onMount(() => {
    if (searchBarEl) {
      animate(searchBarEl, { opacity: [0, 1], y: [-16, 0] }, { duration: 0.4, ease: 'easeOut' });
    }
  });

  function handleSearch() {
    if (!query.trim()) return;
    search(query.trim(), searchType, env);
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') handleSearch();
  }
</script>

<div bind:this={searchBarEl} class="sticky top-0 z-50 border-b border-border bg-bg-panel/80 backdrop-blur-xl px-6 py-3 {$searchState.loading ? 'animate-shimmer' : ''}" style="{$searchState.loading ? 'background-image: linear-gradient(90deg, transparent 0%, rgba(236,114,17,0.08) 50%, transparent 100%);' : ''}"
>
  <div class="flex items-center gap-4">
    <!-- Home button -->
    <button onclick={goHome} class="shrink-0 text-text-muted hover:text-accent transition-colors" aria-label="Back to tools" title="Back to tools">
      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" /></svg>
    </button>
    <!-- Logo / Title -->
    <div class="flex items-center gap-2 shrink-0">
      <div class="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
        <svg class="w-4 h-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <span class="text-sm font-semibold text-text-primary tracking-wide">Campaign Debugger</span>
    </div>

    <!-- Search Type Selector -->
    <select
      bind:value={searchType}
      aria-label="Search type"
      class="bg-bg-input border border-border rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-accent focus:ring-1 focus:ring-accent/50 transition-colors cursor-pointer"
    >
      <option value="advertiser_id">Advertiser ID</option>
      <option value="campaign_id">Campaign ID</option>
    </select>

    <!-- Search Input -->
    <div class="relative flex-1 max-w-xl">
      <input
        type="text"
        bind:value={query}
        onkeydown={handleKeydown}
        placeholder="Enter {searchType === 'advertiser_id' ? 'advertiser' : 'campaign'} ID..."
        class="w-full bg-bg-input border border-border rounded-lg pl-4 pr-10 py-2 text-sm text-text-primary placeholder:text-text-muted outline-none focus:border-accent focus:ring-1 focus:ring-accent/50 transition-all"
      />
      {#if query}
        <button
          onclick={() => query = ''}
          aria-label="Clear search"
          class="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      {/if}
    </div>

    <!-- Environment Toggle -->
    <div class="flex items-center bg-bg-input border border-border rounded-full p-0.5">
      <button
        onclick={() => env = 'Beta'}
        class="px-4 py-1.5 rounded-full text-xs font-medium transition-all {env === 'Beta' ? 'bg-accent text-white shadow-lg shadow-accent/25' : 'text-text-secondary hover:text-text-primary'}"
      >
        Beta
      </button>
      <button
        onclick={() => env = 'Prod'}
        class="px-4 py-1.5 rounded-full text-xs font-medium transition-all {env === 'Prod' ? 'bg-error text-white shadow-lg shadow-error/25' : 'text-text-secondary hover:text-text-primary'}"
      >
        Prod
      </button>
    </div>

    <!-- Search Button -->
    <button
      onclick={handleSearch}
      disabled={!query.trim() || $searchState.loading}
      class="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed
        {$searchState.loading ? 'bg-accent/60' : 'bg-gradient-to-r from-accent to-accent-hover hover:shadow-lg hover:shadow-accent/25 active:scale-[0.93]'}"
    >
      {#if $searchState.loading}
        <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        Searching...
      {:else}
        Search
      {/if}
    </button>
  </div>

  <!-- Error Banner -->
  {#if $searchState.error}
    <div class="mt-2 px-4 py-2 rounded-lg bg-error/10 border border-error/30 text-error text-sm flex items-center gap-2 animate-slide-down">
      <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
      {$searchState.error}
    </div>
  {/if}
</div>
